import React, { useState, useEffect } from "react";
import { useHistory, useLocation, Redirect } from "react-router-dom";
import JobManager from "../components/JobManager";
import ContestacaoInputDisplay from "../components/ContestacaoInputDisplay";
import ContestacaoOutputDisplay from "../components/ContestacaoOutputDisplay";
import CellParamsModal from "../components/CellParamsModal";
import {
  Section,
  MetaGrid,
  MetaRow,
  JobMetaSection,
} from "../components/JobDetailsDrawer";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import {
  Settings,
  Archive,
  Unarchive,
  Sync,
  DeleteOutline,
  RecordVoiceOver,
  Description,
  AssignmentTurnedIn,
  PlaylistAddCheck,
  MenuBook,
} from "@material-ui/icons";

import { getApiBaseUrl } from "../config";
import SoftDeleteJobDialog from "../components/SoftDeleteJobDialog";
import { jobAllowsManualReprocess } from "../utils/jobReprocess";
import { useUserHasProduct } from "../hooks/useUserHasProduct";
import {
  getJobStatusLabel,
  isAwaitingTranscriptionReview,
} from "../utils/jobTaskStatus";

const API_BASE_URL = getApiBaseUrl();

const PRODUCT_CODE_DOSSIE = "DC_CONTESTACAO_DOSSIE";

const SOFT_DELETE_ALLOWED_STATUSES = ["DONE", "FINAL_ERROR", "ERROR_FINALIZING"];

const ContestacaoDossiePage = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation("jobs");
  const { cell, hasRole, user, token } = useAuth();
  const { loading: loadingProduct, hasProduct: hasDossieProduct } =
    useUserHasProduct(PRODUCT_CODE_DOSSIE);

  const [cellParamsModalOpen, setCellParamsModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [initialJobId, setInitialJobId] = useState(null);
  const [softDeleteTarget, setSoftDeleteTarget] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jobId = params.get("jobId");
    if (jobId) {
      setInitialJobId(parseInt(jobId, 10));
      history.replace("/contestacao-dossie");
    }
  }, [location.search, history]);

  /* Mantemos somente as colunas essenciais para identificação rápida do job;
     célula, total de páginas e arquivos de entrada migram para o drawer
     lateral acionado pelo botão ">" à direita. */
  const columns = [
    {
      key: "id",
      label: t("contestacao.columns.id"),
      type: "id",
      sortable: false,
    },
    {
      key: "insertion_timestamp",
      label: t("contestacao.columns.dateTime"),
      type: "date",
      sortable: true,
    },
    {
      key: "job_name",
      label: t("table.taskName"),
      type: "text",
      sortable: true,
    },
    {
      key: "status",
      label: t("contestacao.columns.status"),
      type: "status",
      sortable: true,
      align: "center",
      formatValue: (value, job) => getJobStatusLabel(value, job, t),
    },
    {
      key: "user_name",
      label: t("contestacao.columns.user"),
      type: "user",
      sortable: true,
    },
    {
      key: "output_payload",
      label: t("contestacao.columns.generatedDocument"),
      type: "custom",
      sortable: false,
      align: "center",
      renderCell: (job) => (
        <ContestacaoOutputDisplay
          outputPayload={job.output_payload}
          status={job.status}
          job={job}
        />
      ),
    },
    {
      key: "actions",
      label: t("table.actions"),
      type: "actions",
      sortable: false,
    },
  ];

  const filterFields = [
    {
      key: "job_id",
      label: t("filters.jobId"),
      type: "text",
      placeholder: t("filters.jobIdPlaceholder"),
    },
    {
      key: "job_name_filter",
      label: t("filters.taskName"),
      type: "text",
      placeholder: t("filters.taskNamePlaceholder"),
    },
    {
      key: "external_id",
      label: t("filters.externalId"),
      type: "text",
      placeholder: t("filters.externalIdPlaceholder"),
    },
    {
      key: "date_start",
      label: t("filters.dateStart"),
      type: "date",
    },
    {
      key: "date_end",
      label: t("filters.dateEnd"),
      type: "date",
    },
    {
      key: "user_name_filter",
      label: t("filters.user"),
      type: "text",
      placeholder: t("filters.userPlaceholder"),
    },
    {
      key: "status_filter",
      label: t("filters.status"),
      type: "select",
      options: [
        { value: "WAITING", label: t("status.WAITING") },
        { value: "RUNNING", label: t("status.RUNNING") },
        { value: "DONE", label: t("status.DONE") },
        { value: "ERROR", label: t("status.ERROR") },
        { value: "FINAL_ERROR", label: t("status.FINAL_ERROR") },
        { value: "DISCARDED", label: t("status.DISCARDED") },
      ],
    },
    {
      key: "task_type_filter",
      label: t("filters.taskType"),
      type: "select",
      options: [
        {
          value: "DOSSIER_CREATION",
          label: t("jobStatusViewer.taskTypes.DOSSIER_CREATION"),
        },
        {
          value: "DOSSIER_ANALYSIS",
          label: t("jobStatusViewer.taskTypes.DOSSIER_ANALYSIS"),
        },
        {
          value: "CONTESTATION_CREATION",
          label: t("jobStatusViewer.taskTypes.CONTESTATION_CREATION"),
        },
        {
          value: "CONTESTATION_REVIEW",
          label: t("jobStatusViewer.taskTypes.CONTESTATION_REVIEW"),
        },
        {
          value: "FINAL_DOC_CREATION",
          label: t("jobStatusViewer.taskTypes.FINAL_DOC_CREATION"),
        },
      ],
    },
    {
      key: "output_payload_text_filter",
      label: t("contestacao.filters.documentContent"),
      type: "text",
      placeholder: t("contestacao.filters.documentContentPlaceholder"),
    },
    {
      key: "include_archived",
      label: t("filters.showArchived"),
      type: "select",
      options: [
        { value: "", label: t("filters.showArchivedNo") },
        { value: "true", label: t("filters.showArchivedYes") },
      ],
    },
  ];


  const apiHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ServiceName: "DOCUMENT_CREATION",
  };

  const handleArchiveJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/archive`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao arquivar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao arquivar tarefa:", error);
    }
  };

  const handleUnarchiveJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/unarchive`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao desarquivar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao desarquivar tarefa:", error);
    }
  };

  const handleSoftDeleteJob = async (job) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/job/${job.id}/soft_delete`,
        {
          method: "PATCH",
          headers: apiHeaders,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao ocultar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao ocultar tarefa:", error);
    }
  };

  const handleBatchArchive = async (jobIds) => {
    if (!jobIds.length) return;
    try {
      const response = await fetch(`${API_BASE_URL}/job/batch_archive`, {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({ job_ids: jobIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao arquivar em lote");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao arquivar em lote:", error);
    }
  };

  const handleBatchUnarchive = async (jobIds) => {
    if (!jobIds.length) return;
    try {
      const response = await fetch(`${API_BASE_URL}/job/batch_unarchive`, {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({ job_ids: jobIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao desarquivar em lote");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao desarquivar em lote:", error);
    }
  };

  const handleReprocessJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/reprocess`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao reprocessar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao reprocessar tarefa:", error);
    }
  };

  const canReprocess = hasRole("ADMIN", "re_execute_job");
  const canArchive = hasRole("DOCUMENT_CREATION", "archive_job");
  const canDelete = hasRole("DOCUMENT_CREATION", "delete_job");

  /* Ver `ContestacaoPage` para a semântica dos tiers. */
  const customActions = [
    {
      key: "transcription_review",
      tier: "primary",
      label: t("actions.primary.transcriptionReview"),
      icon: <RecordVoiceOver />,
      tooltip: t("actions.transcriptionReviewTooltip"),
      hidden: (job) => !isAwaitingTranscriptionReview(job),
    },
    ...(canReprocess
      ? [
          {
            key: "reprocess",
            tier: "primary",
            label: t("actions.primary.retry"),
            icon: <Sync />,
            tooltip: t("actions.reprocessTooltip"),
            hidden: (job) => !jobAllowsManualReprocess(job),
            onClick: (job) => handleReprocessJob(job),
          },
        ]
      : []),
    {
      key: "archive",
      tier: "menu",
      icon: <Archive />,
      label: t("actions.archive"),
      tooltip: t("actions.archiveTooltip"),
      hidden: (job) =>
        !canArchive ||
        !!job.archived_timestamp ||
        !["DONE", "FINAL_ERROR", "ERROR_FINALIZING"].includes(job.status),
      onClick: (job) => handleArchiveJob(job),
    },
    {
      key: "unarchive",
      tier: "menu",
      icon: <Unarchive />,
      label: t("actions.unarchive"),
      tooltip: t("actions.unarchiveTooltip"),
      hidden: (job) => !canArchive || !job.archived_timestamp,
      onClick: (job) => handleUnarchiveJob(job),
    },
    {
      key: "soft_delete",
      tier: "danger",
      icon: <DeleteOutline />,
      label: t("actions.softDelete"),
      tooltip: t("actions.softDeleteTooltip"),
      hidden: (job) =>
        !canDelete ||
        !SOFT_DELETE_ALLOWED_STATUSES.includes(job.status),
      onClick: (job) => setSoftDeleteTarget(job),
    },
  ];

  const handleNewJob = () => {
    history.push("/contestacao-dossie/nova");
  };

  const handleJobClick = (job) => {
    history.push(`/contestacao-dossie/detalhes/${job.id}`);
  };

  const handleJobAction = (action, job) => {
    switch (action) {
      case "view":
        history.push(`/contestacao-dossie/detalhes/${job.id}`);
        break;

      case "download":
        downloadContestacaoDocument(job);
        break;

      case "review":
        history.push(`/documento/revisar/contestacao/${job.id}`);
        break;

      case "transcription_review":
        history.push(`/transcricao/revisar/${job.id}`);
        break;

      case "archive":
        handleArchiveJob(job);
        break;

      case "unarchive":
        handleUnarchiveJob(job);
        break;

      case "soft_delete":
        setSoftDeleteTarget(job);
        break;

      case "reprocess":
        handleReprocessJob(job);
        break;

      case "regenerate":
        regenerateDocument(job);
        break;

      default:
        break;
    }
  };

  const downloadContestacaoDocument = (job) => {
    try {
      const payload =
        typeof job.output_payload === "string"
          ? JSON.parse(job.output_payload)
          : job.output_payload;

      // Novo formato: result_files array
      const files = payload?.result_files;
      if (files?.length) {
        files.forEach((f) => {
          const url = `${API_BASE_URL}/files/download?token=${encodeURIComponent(f.token)}&Servicename=DOCUMENT_CREATION`;
          const link = document.createElement("a");
          link.href = url;
          link.download = f.filename || "dossie.pdf";
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
        return;
      }

      // Fallback: formato antigo result_docx
      const docx = payload?.result_docx;
      if (docx?.token) {
        const url = `${API_BASE_URL}/files/download?token=${encodeURIComponent(docx.token)}&Servicename=DOCUMENT_CREATION`;
        const link = document.createElement("a");
        link.href = url;
        link.download = docx.filename || "documento.docx";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Erro ao fazer download:", error);
    }
  };

  const regenerateDocument = (job) => {
    console.log("Regenerando documento para contestação:", job.id);
  };

  const hasContestParamsPermission = () => {
    return (
      hasRole(
        "DOCUMENT_CREATION",
        "edit_custom_default",
        PRODUCT_CODE_DOSSIE,
      ) ||
      hasRole(
        "DOCUMENT_CREATION",
        "edit_custom_super_user",
        PRODUCT_CODE_DOSSIE,
      )
    );
  };

  const handleOpenCellParams = () => {
    setCellParamsModalOpen(true);
  };

  const handleCloseCellParams = () => {
    setCellParamsModalOpen(false);
  };

  const customHeaderActions = hasContestParamsPermission()
    ? [
        {
          icon: <Settings />,
          onClick: handleOpenCellParams,
          disabled: !cell?.value,
          style: {
            color: "var(--ikaro-orange-primary)",
          },
        },
      ]
    : [];

  /* Conteúdo do drawer lateral. Reutiliza o `JobMetaSection` para os campos
     comuns e adiciona seções específicas do dossiê (etapa do fluxo,
     arquivos de entrada expandidos e documento gerado). */
  const renderDossieDetails = (job) => {
    const totalPages = job?.input_payload?.total_paginas;
    const taskLabel = job?.task
      ? t(`jobStatusViewer.taskTypes.${job.task}`, job.task)
      : null;
    const statusLabel = getJobStatusLabel(job?.status, job, t);
    return (
      <>
        <JobMetaSection job={job} />

        <Section
          title={t("contestacao.drawer.sections.task")}
          accent="neutral"
        >
          <MetaGrid>
            <MetaRow
              icon={<AssignmentTurnedIn />}
              label={t("contestacao.drawer.fields.taskName")}
              value={job?.job_name || null}
              copyValue={job?.job_name || null}
            />
            <MetaRow
              icon={<PlaylistAddCheck />}
              label={t("contestacao.drawer.fields.task")}
              value={taskLabel}
              copyValue={null}
            />
            <MetaRow
              icon={<MenuBook />}
              label={t("contestacao.drawer.fields.statusDetail")}
              value={statusLabel}
              copyValue={null}
            />
            <MetaRow
              icon={<Description />}
              label={t("contestacao.drawer.fields.totalPages")}
              value={totalPages != null ? String(totalPages) : null}
              copyValue={null}
              emptyText={t("contestacao.drawer.fields.totalPagesEmpty")}
            />
          </MetaGrid>
        </Section>

        <Section
          title={t("contestacao.drawer.sections.input")}
          accent="primary"
        >
          <ContestacaoInputDisplay
            inputPayload={job?.input_payload}
            forceExpanded
          />
        </Section>

        <Section
          title={t("contestacao.drawer.sections.output")}
          accent="secondary"
        >
          <ContestacaoOutputDisplay
            outputPayload={job?.output_payload}
            status={job?.status}
            job={job}
          />
        </Section>
      </>
    );
  };

  if (loadingProduct) {
    return null;
  }

  if (!hasDossieProduct) {
    return <Redirect to="/contestacoes" />;
  }

  return (
    <>
      <JobManager
        jobType="contestacao_dossie"
        title={t("contestacaoDossie.title")}
        subtitle={t("contestacaoDossie.subtitle")}
        apiEndpoint="/job/get_jobs_list"
        createJobPath="/contestacao-dossie/nova"
        columns={columns}
        filterFields={filterFields}
        customActions={customActions}
        customHeaderActions={customHeaderActions}
        onNewJob={handleNewJob}
        onJobClick={handleJobClick}
        onJobAction={handleJobAction}
        code={PRODUCT_CODE_DOSSIE}
        serviceName="DOCUMENT_CREATION"
        newJobButtonLabel={t("contestacaoDossie.newContestation")}
        filterButtonLabel={t("header.filters")}
        showNewJobButton={
          hasRole("DOCUMENT_CREATION", "exec_process") && hasDossieProduct
        }
        hideDefaultActions={true}
        refreshTrigger={refreshTrigger}
        enableSelection={true}
        onBatchArchive={handleBatchArchive}
        onBatchUnarchive={handleBatchUnarchive}
        initialJobId={initialJobId}
        renderJobDetails={renderDossieDetails}
        drawerTitle={t("contestacaoDossie.drawer.title")}
      />

      <CellParamsModal
        open={cellParamsModalOpen}
        onClose={handleCloseCellParams}
        cellId={cell?.value}
        variant="dossie"
      />

      <SoftDeleteJobDialog
        open={softDeleteTarget != null}
        job={softDeleteTarget}
        onClose={() => setSoftDeleteTarget(null)}
        onConfirm={handleSoftDeleteJob}
      />
    </>
  );
};

export default ContestacaoDossiePage;
